import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import RequestCategoryModal from './RequestCategoryModal';

function IdentitySelector({ userId, onIdentitiesChange, country, country2 }) {
  const [categories, setCategories] = useState([]);
  const [userIdentities, setUserIdentities] = useState([]);
  const [identitiesByCategory, setIdentitiesByCategory] = useState({});
  const [casteSearchTerm, setCasteSearchTerm] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const isFirstLoad = useRef(true);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!loadedRef.current) {
      loadedRef.current = true;
      loadData();
    }
  }, []);

  useEffect(() => {
    if (loadedRef.current) {
      loadData();
    }
  }, [showAllCategories]);

  const loadData = async () => {
    try {
      if (isFirstLoad.current) setInitialLoading(true);
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      const params = new URLSearchParams();
      if (!showAllCategories) {
        if (country) params.append('country', country);
        if (country2 && country2 !== country) params.append('country2', country2);
      }
      const qs = params.toString();
      const catRes = await axios.get(`/api/identities/categories${qs ? `?${qs}` : ''}`);
      setCategories(catRes.data.categories);

      const uiRes = await axios.get('/api/identities/me', { headers });
      setUserIdentities(uiRes.data.identities);

      const idMap = {};
      await Promise.all(catRes.data.categories.map(async (cat) => {
        const r = await axios.get(`/api/identities/categories/${cat.category_id}/identities`);
        idMap[cat.category_id] = r.data.identities;
      }));
      setIdentitiesByCategory(idMap);
      isFirstLoad.current = false;
      setInitialLoading(false);
    } catch (err) {
      console.error('Error loading identities:', err);
      setError('Failed to load identities');
      isFirstLoad.current = false;
      setInitialLoading(false);
    }
  };

  const refreshUserIdentities = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get('/api/identities/me', { headers });
      setUserIdentities(res.data.identities);
    } catch (e) { /* silent */ }
  };

  const handleAdd = async (categoryId, identityId, categoryName) => {
    const token = localStorage.getItem('accessToken');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (categoryName !== 'caste' && userIdentities.length >= 4) {
        setError('Maximum 4 identities allowed. Remove one to add another.');
        return;
      }
      const isPrimary = userIdentities.length === 0;
      if (categoryName === 'caste') {
        const existing = userIdentities.find(ui => ui.category_name === 'caste');
        if (existing) {
          await axios.delete(`/api/identities/me/${existing.identity_id}`, { headers });
          setUserIdentities(prev => prev.filter(ui => ui.category_name !== 'caste'));
        }
      }
      await axios.post('/api/identities/me', { identityId: parseInt(identityId), isPrimary }, { headers });
      // Optimistic add
      const cat = categories.find(c => c.category_id === categoryId);
      const id = (identitiesByCategory[categoryId] || []).find(i => i.identity_id === identityId);
      if (id && cat) {
        setUserIdentities(prev => [...prev, {
          user_identity_id: Date.now(), identity_id: identityId, identity_name: id.name,
          category_id: categoryId, category_name: cat.name, category_display_name: cat.display_name,
          category_icon: cat.icon, category_color: cat.color, is_primary: isPrimary
        }]);
      }
      setError('');
      if (onIdentitiesChange) onIdentitiesChange();
      refreshUserIdentities();
    } catch (err) {
      setError('Failed to add identity');
      refreshUserIdentities();
    }
  };

  const handleRemove = async (identityId) => {
    const token = localStorage.getItem('accessToken');
    const headers = { Authorization: `Bearer ${token}` };
    setUserIdentities(prev => prev.filter(ui => ui.identity_id !== identityId));
    try {
      await axios.delete(`/api/identities/me/${identityId}`, { headers });
      if (onIdentitiesChange) onIdentitiesChange();
      refreshUserIdentities();
    } catch (err) {
      setError('Failed to remove identity');
      refreshUserIdentities();
    }
  };

  const handleSetPrimary = async (identityId) => {
    const token = localStorage.getItem('accessToken');
    const headers = { Authorization: `Bearer ${token}` };
    setUserIdentities(prev => prev.map(ui => ({ ...ui, is_primary: ui.identity_id === identityId })));
    try {
      await axios.put(`/api/identities/me/${identityId}/primary`, {}, { headers });
      if (onIdentitiesChange) onIdentitiesChange();
      refreshUserIdentities();
    } catch (err) {
      setError('Failed to set primary');
    }
  };

  const isSel = (identityId) => userIdentities.some(ui => ui.identity_id === identityId);

  const getSearchResults = () => {
    if (!searchTerm || searchTerm.length < 2) return [];
    const term = searchTerm.toLowerCase();
    const ids = userIdentities.map(ui => ui.identity_id);
    const results = [];
    for (const cat of categories) {
      for (const id of (identitiesByCategory[cat.category_id] || [])) {
        if (ids.includes(id.identity_id)) continue;
        if (id.name.toLowerCase().includes(term) || cat.display_name.toLowerCase().includes(term)) {
          results.push({ ...id, category: cat });
        }
      }
    }
    return results.slice(0, 15);
  };

  // Render the bottom sheet modal via Portal (outside any form)
  const renderModal = () => {
    if (!expandedCategory) return null;
    const category = categories.find(c => c.category_id === expandedCategory);
    if (!category) return null;
    const catIdentities = identitiesByCategory[category.category_id] || [];
    const isCaste = category.name === 'caste';

    return ReactDOM.createPortal(
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
      }} onClick={() => { setExpandedCategory(null); setCasteSearchTerm(''); }}>
        <div style={{
          backgroundColor: 'white', borderRadius: '16px 16px 0 0',
          width: '100%', maxWidth: '500px', maxHeight: '70vh',
          display: 'flex', flexDirection: 'column'
        }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>{category.icon}</span>
              <strong>{category.display_name}</strong>
            </div>
            <button type="button" onClick={() => { setExpandedCategory(null); setCasteSearchTerm(''); }}
              style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666' }}>✕</button>
          </div>
          {isCaste && (
            <div style={{ padding: '8px 16px 0' }}>
              <input type="text" placeholder="Search..." value={casteSearchTerm}
                onChange={(e) => setCasteSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
          )}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 16px' }}>
            {catIdentities
              .filter(id => !(isCaste && casteSearchTerm) || id.name.toLowerCase().includes(casteSearchTerm.toLowerCase()))
              .map(identity => {
                const selected = isSel(identity.identity_id);
                return (
                  <div key={identity.identity_id}
                    onClick={() => selected ? handleRemove(identity.identity_id) : handleAdd(category.category_id, identity.identity_id, category.name)}
                    style={{
                      padding: '10px 12px', marginBottom: '4px',
                      backgroundColor: selected ? (category.color || '#7c3aed') + '20' : 'white',
                      border: selected ? `2px solid ${category.color || '#7c3aed'}` : '1px solid #eee',
                      borderRadius: '8px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                    <span style={{ fontSize: '16px' }}>{selected ? '✓' : '○'}</span>
                    <span style={{ fontSize: '14px' }}>{identity.name}</span>
                  </div>
                );
              })}
          </div>
          <div style={{ padding: '10px 16px', borderTop: '1px solid #eee' }}>
            <button type="button" onClick={() => { setExpandedCategory(null); setCasteSearchTerm(''); }}
              style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '9999px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}>
              Done
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="identity-selector">
      <h3>Your Identities (Encrypted)</h3>
      <p style={{ color: '#666', fontSize: '14px', marginBottom: '12px' }}>
        Choose the identities that matter to you. Pick at least 1, max 4.
      </p>

      {initialLoading && <p style={{ color: '#7c3aed', fontSize: '13px', marginBottom: '12px' }}>Loading identities...</p>}

      {error && <div className="error" style={{ marginBottom: '12px' }}>{error}</div>}

      <div style={{
        padding: '12px', backgroundColor: userIdentities.length >= 4 ? '#fff3cd' : '#e7f3ff',
        border: `1px solid ${userIdentities.length >= 4 ? '#ffc107' : '#b3d9ff'}`,
        borderRadius: '6px', marginBottom: '12px', fontSize: '14px'
      }}>
        <strong>{userIdentities.length} / 4 identities selected</strong>
        {userIdentities.length >= 4 && <span style={{ color: '#856404', marginLeft: '8px' }}>(Maximum reached)</span>}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '12px', position: 'relative' }}>
        <input type="text" placeholder="🔍 Search any identity..." value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '2px solid #7c3aed', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }} />
        {searchTerm && <button type="button" onClick={() => setSearchTerm('')}
          style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#999' }}>✕</button>}
        {(() => {
          const results = getSearchResults();
          if (searchTerm.length >= 2 && results.length > 0) return (
            <div style={{ marginTop: '8px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: 'white', maxHeight: '300px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              {results.map(item => (
                <div key={item.identity_id} onClick={() => { handleAdd(item.category.category_id, item.identity_id, item.category.name); setSearchTerm(''); }}
                  style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div><span style={{ fontWeight: '500' }}>{item.name}</span><span style={{ color: '#888', fontSize: '13px', marginLeft: '8px' }}>{item.category.icon} {item.category.display_name}</span></div>
                  <span style={{ backgroundColor: '#7c3aed', color: 'white', padding: '3px 10px', borderRadius: '12px', fontSize: '12px' }}>+ Add</span>
                </div>
              ))}
            </div>
          );
          if (searchTerm.length >= 2 && results.length === 0) return <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '8px', color: '#888', fontSize: '14px', textAlign: 'center' }}>No matching identities found.</div>;
          return null;
        })()}
        <div style={{ textAlign: 'right', marginTop: '4px' }}>
          <button type="button" onClick={() => setShowAllCategories(!showAllCategories)}
            style={{ background: 'none', border: 'none', color: '#7c3aed', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>
            {showAllCategories ? '← Show local identities' : 'Show all countries →'}
          </button>
        </div>
      </div>

      {/* Chips */}
      {userIdentities.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
          {userIdentities.map(ui => (
            <span key={ui.user_identity_id} style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
              backgroundColor: (ui.category_color || '#7c3aed') + '20', border: `1px solid ${ui.category_color || '#7c3aed'}`,
              borderRadius: '16px', fontSize: '12px'
            }}>
              {ui.category_icon} {ui.identity_name}
              {ui.is_primary && <span style={{ backgroundColor: ui.category_color || '#7c3aed', color: 'white', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 'bold' }}>★</span>}
              {!ui.is_primary && <button type="button" onClick={() => handleSetPrimary(ui.identity_id)} title="Set as primary" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#999', padding: 0 }}>☆</button>}
              <button type="button" onClick={() => handleRemove(ui.identity_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#999', padding: 0, lineHeight: 1 }}>✕</button>
            </span>
          ))}
        </div>
      )}

      {/* Category grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '12px' }}>
        {categories.map(cat => {
          const ids = identitiesByCategory[cat.category_id] || [];
          const cnt = userIdentities.filter(ui => ids.some(ci => ci.identity_id === ui.identity_id)).length;
          return (
            <div key={cat.category_id} onClick={() => setExpandedCategory(cat.category_id)}
              style={{ padding: '12px', borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
                border: `2px solid ${cnt > 0 ? cat.color : '#e0e0e0'}`, backgroundColor: cnt > 0 ? cat.color + '10' : 'white', position: 'relative' }}>
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>{cat.icon}</div>
              <div style={{ fontSize: '12px', fontWeight: '600' }}>{cat.display_name}</div>
              {cnt > 0 && <span style={{ position: 'absolute', top: '4px', right: '4px', backgroundColor: cat.color, color: 'white', width: '18px', height: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold' }}>{cnt}</span>}
            </div>
          );
        })}
      </div>

      {renderModal()}

      {userIdentities.length === 0 && !initialLoading && (
        <div style={{ padding: '16px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', marginTop: '12px' }}>
          <strong>Get Started</strong>
          <p style={{ marginTop: '8px', fontSize: '14px' }}>Tap a category above to add your first identity.</p>
        </div>
      )}

      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>Don't see your identity?</p>
        <button type="button" onClick={() => setShowRequestModal(true)}
          style={{ backgroundColor: '#9b59b6', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
          Request New Category
        </button>
      </div>

      <RequestCategoryModal isOpen={showRequestModal} onClose={() => setShowRequestModal(false)}
        onSuccess={() => { setShowRequestModal(false); setError(''); alert("Category request submitted! You'll be notified when it's reviewed."); }} />
    </div>
  );
}

export default IdentitySelector;
